import { useState, useCallback } from 'react'
import { Clock, Loader2 } from 'lucide-react'

function formatDuration(sec) {
  if (sec >= 60) return `${Math.round(sec / 60)}m`
  return `${sec}s`
}

export default function LateStartBand({ lateStart, meetingId, onResolve, onUndo, onRefresh }) {
  const [busy, setBusy] = useState(null)
  const [dismissed, setDismissed] = useState(false)
  const [flash, setFlash] = useState(null)

  const handleUndo = useCallback(async () => {
    setBusy('undo')
    try {
      await onUndo(meetingId)
      setFlash('Original timing restored')
      await onRefresh()
    } catch {
      setFlash('Failed — try again')
    } finally {
      setBusy(null)
      setTimeout(() => setFlash(null), 2500)
    }
  }, [meetingId, onUndo, onRefresh])

  const handleChoice = useCallback(async (choice) => {
    setBusy(choice)
    try {
      await onResolve(meetingId, choice)
      setFlash(choice === 'compress' ? 'Agenda compressed' : 'Choice applied')
      await onRefresh()
    } catch {
      setFlash('Failed — try again')
    } finally {
      setBusy(null)
      setTimeout(() => setFlash(null), 2500)
    }
  }, [meetingId, onResolve, onRefresh])

  if (!lateStart || dismissed) return null

  const { tier, lateMinutes, resolution, canUndo, itemAdjustments } = lateStart

  // ── ABSORBED: single-line subtle indicator ──
  if (tier === 'absorbed') {
    if (!canUndo) return null
    return (
      <div className="flex items-center gap-2 rounded-lg bg-slate-50 px-3 py-1.5">
        <Clock className="h-3.5 w-3.5 text-slate-400 shrink-0" />
        <span className="text-sm text-slate-500 flex-1">
          Adjusted for {lateMinutes} min late start
        </span>
        <button
          type="button"
          disabled={busy === 'undo'}
          onClick={handleUndo}
          className="text-xs font-medium text-slate-500 hover:text-slate-700 underline decoration-dotted transition-colors disabled:opacity-50"
        >
          {busy === 'undo' ? <Loader2 className="h-3 w-3 animate-spin inline" /> : 'Undo'}
        </button>
      </div>
    )
  }

  // ── NUDGE: compact indicator with undo ──
  if (tier === 'nudge') {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-amber-200/60 bg-amber-50/40 px-3 py-2">
        <Clock className="h-3.5 w-3.5 text-amber-500 shrink-0" />
        <span className="text-sm text-slate-700 flex-1">
          Started {lateMinutes} min late — timing adjusted
        </span>
        {canUndo ? (
          <button
            type="button"
            disabled={busy === 'undo'}
            onClick={handleUndo}
            className="rounded-md border border-slate-200 bg-white px-2 py-0.5 text-xs font-medium text-slate-600 hover:bg-slate-50 transition-colors disabled:opacity-50"
          >
            {busy === 'undo' ? <Loader2 className="h-3 w-3 animate-spin inline" /> : 'Keep originals'}
          </button>
        ) : null}
      </div>
    )
  }

  // ── GUIDED / TRIAGE: card with adjustment details + override options ──
  const hasAdjustments = itemAdjustments && itemAdjustments.length > 0
  const isCompressed = resolution === 'compress' || resolution === 'rithmo_suggests'
  const isKeptOriginal = resolution === 'keep_durations'

  return (
    <div className={`rounded-xl border p-3 space-y-2 transition-colors ${
      isKeptOriginal
        ? 'border-slate-200 bg-slate-50/60'
        : 'border-amber-300 bg-amber-50/60'
    }`}>
      {/* Header */}
      <div className="flex items-center gap-2">
        <Clock className={`h-4 w-4 shrink-0 ${isKeptOriginal ? 'text-slate-400' : 'text-amber-500'}`} />
        <span className="text-sm font-semibold text-slate-900">
          Started {lateMinutes} min late
        </span>
        <span className={`ml-auto rounded-full px-2 py-0.5 text-xs font-semibold ${
          isKeptOriginal
            ? 'bg-slate-100 text-slate-600'
            : 'bg-amber-100 text-amber-700'
        }`}>
          {isCompressed ? 'Compressed' : isKeptOriginal ? 'Original timing' : `${Math.round(lateStart.impactRatio * 100)}% impact`}
        </span>
      </div>

      {/* Per-item adjustments (only show when compressed) */}
      {hasAdjustments && isCompressed ? (
        <div className="space-y-1.5">
          {itemAdjustments.slice(0, 4).map((adj) => (
            <div key={adj.agendaItemId} className="flex items-center gap-2 text-xs">
              <span className="flex-1 truncate text-slate-600">{adj.title}</span>
              <span className="text-slate-400 line-through">{formatDuration(adj.originalDurationSec)}</span>
              <span className="font-medium text-slate-700">{formatDuration(adj.adjustedDurationSec)}</span>
            </div>
          ))}
          {itemAdjustments.length > 4 ? (
            <p className="text-xs text-slate-400">+{itemAdjustments.length - 4} more adjusted</p>
          ) : null}
        </div>
      ) : null}

      {/* Flash confirmation */}
      {flash ? (
        <p className="text-sm font-medium text-emerald-600">{flash}</p>
      ) : (
        <p className="text-xs text-slate-500">
          {isKeptOriginal
            ? 'Original durations kept — end time shifted.'
            : isCompressed
              ? 'Agenda compressed — key items protected.'
              : 'Agenda auto-adjusted — key items protected. Override below if needed.'}
        </p>
      )}

      {/* Action buttons */}
      <div className="flex items-center gap-2">
        {isCompressed && canUndo ? (
          <button
            type="button"
            disabled={!!busy}
            onClick={handleUndo}
            className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-50"
          >
            {busy === 'undo' ? <Loader2 className="h-3 w-3 animate-spin inline mr-1" /> : null}
            Revert to original agenda timings
          </button>
        ) : null}
        {isKeptOriginal ? (
          <button
            type="button"
            disabled={!!busy}
            onClick={() => handleChoice('compress')}
            className="rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-800 hover:bg-emerald-100 transition-colors disabled:opacity-50"
          >
            {busy === 'compress' ? <Loader2 className="h-3 w-3 animate-spin inline mr-1" /> : null}
            Compress to Fit
          </button>
        ) : null}
        {!resolution ? (
          <>
            <button
              type="button"
              disabled={!!busy}
              onClick={() => handleChoice('compress')}
              className="rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-800 hover:bg-emerald-100 transition-colors disabled:opacity-50"
            >
              {busy === 'compress' ? <Loader2 className="h-3 w-3 animate-spin inline mr-1" /> : null}
              Compress
            </button>
            <button
              type="button"
              disabled={!!busy}
              onClick={() => handleChoice('keep_durations')}
              className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-50"
            >
              Keep Originals
            </button>
          </>
        ) : null}
      </div>
    </div>
  )
}
